/**
 * Página de login — layout 2 columnas (form + panel propuesta de valor).
 *
 * Diseño basado en mockup Stitch `docs/design/mockups/S1-login.png`
 * adaptado a la realidad actual de Kaptar (sin MFA enforcement aún,
 * sin RNBD registrado; el microcopy refleja lo que SÍ tenemos hoy).
 *
 * Capas de seguridad activas (R-SEC-013 + R-SEC-014 + R-SEC-001):
 * 1. RLS deny-all en todas las tablas `public` (R-SEC-013).
 * 2. Cloudflare Turnstile CAPTCHA — bloquea credential stuffing y
 *    bots distribuidos. Token requerido por Supabase Auth si la
 *    protección está activa en el Dashboard (Settings → Auth →
 *    Captcha protection). Ver R-SEC-014 en `docs/engineering/security.md`.
 * 3. Tenant scoping aplicado en cada query del backend (R-SEC-001).
 *
 * Flujo:
 * 1. Si ya hay sesión, redirige al destino (`state.from`) o `/radar`
 *    (o `/admin` para staff).
 * 2. Submit → `signInWithPassword(email, password, captchaToken)`.
 * 3. Si OK, el `AuthProvider` actualiza la session → redirect.
 * 4. Si error, muestra mensaje humanizado de Supabase.
 *
 * El widget Turnstile es invisible para el 99% de usuarios; solo
 * aparece challenge visible si el comportamiento parece sospechoso.
 */
import { Turnstile, type TurnstileInstance } from "@marsidev/react-turnstile";
import { type FormEvent, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";

import {
  ACCESO_RETIRADO_PARAM,
  ACCESO_RETIRADO_VALUE,
  LOGIN_ACCESO_RETIRADO,
} from "@/lib/acceso-retirado";
import { useAuth } from "@/lib/auth";
import { useMunicipality } from "@/lib/municipality";

interface LocationState {
  from?: string;
}

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY;

export function Login() {
  const { session, signInWithPassword, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { isStaff, isLoading: muniLoading, context } = useMunicipality();

  // Banner de éxito tras reset de password — el ResetPassword nos
  // manda a /login?password_reset=ok para confirmar al user.
  const justResetPassword = searchParams.get("password_reset") === "ok";

  // 7-sep-2026: a quien le retiraron el acceso lo manda aquí el gate (o el
  // wizard) con ?motivo=acceso-retirado. Si vuelve a iniciar sesión más
  // tarde, lo detectamos por el contexto (`access_revoked`) y nos
  // redirigimos a la misma URL: el motivo vive en la URL, no en estado,
  // para que el banner sobreviva al cierre de sesión.
  const accesoRetirado = searchParams.get(ACCESO_RETIRADO_PARAM) === ACCESO_RETIRADO_VALUE;

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileInstance | null>(null);

  const explicitFrom = (location.state as LocationState | null)?.from ?? null;

  useEffect(() => {
    if (!session) return;
    if (accesoRetirado) {
      // Llegó con la sesión viva (el gate solo redirige): se cierra aquí y
      // el banner explica por qué no entra. Sin esto, el redirect de abajo
      // lo devolvería al gate y el gate a aquí, en bucle.
      void signOut();
      return;
    }
    if (muniLoading) return;
    if (context?.access_revoked) {
      // Volvió a iniciar sesión después de que lo quitaran: el siguiente
      // render, ya con el motivo en la URL, cierra la sesión.
      navigate(LOGIN_ACCESO_RETIRADO, { replace: true });
      return;
    }
    const target = explicitFrom ?? (isStaff ? "/admin" : "/radar");
    navigate(target, { replace: true });
  }, [session, explicitFrom, navigate, isStaff, muniLoading, accesoRetirado, context, signOut]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setErrorMsg(null);

    // Si Supabase tiene captcha activado y no hay token, abortar antes
    // de pegarle al endpoint — Supabase respondería con 400 igual.
    if (TURNSTILE_SITE_KEY && !captchaToken) {
      setErrorMsg("Espera a que termine la verificación de seguridad antes de continuar.");
      return;
    }

    setIsSubmitting(true);
    const { error } = await signInWithPassword(email, password, captchaToken);
    setIsSubmitting(false);

    if (error) {
      setErrorMsg(humanizeError(error));
      // Reset del token: Cloudflare invalida tokens consumidos. Forzamos
      // un nuevo challenge para el próximo intento.
      turnstileRef.current?.reset();
      setCaptchaToken(null);
    }
    // Si success, el useEffect de arriba redirige cuando `session` llegue.
  }

  return (
    <div className="grid min-h-screen grid-cols-1 lg:grid-cols-4">
      {/* Columna izquierda: form (3/4 del ancho en desktop) */}
      <main className="flex items-center justify-center bg-surface p-6 sm:p-10 lg:col-span-3">
        <div className="w-full max-w-md">
          <a
            href="https://kaptar.com.co"
            className="mb-6 inline-flex items-center gap-1 text-sm text-slate-500 transition-colors hover:text-brand-700"
          >
            <span aria-hidden="true">←</span>
            <span>Volver a kaptar.com.co</span>
          </a>

          <header className="mb-8 flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-600">
              <svg viewBox="0 0 24 24" fill="none" className="h-6 w-6">
                <path d="M12 3L4 8l8 5 8-5-8-5z" fill="white" opacity="0.9" />
                <path d="M4 13l8 5 8-5" stroke="white" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
            </div>
            <div>
              <h1 className="text-2xl font-extrabold tracking-tight text-slate-900">Kaptar</h1>
              <p className="text-xs text-slate-500">
                La forma inteligente de captar recursos públicos
              </p>
            </div>
          </header>

          <form
            onSubmit={handleSubmit}
            className="rounded-2xl border border-slate-200 bg-white p-6 shadow-xs"
            aria-labelledby="login-title"
          >
            <h2 id="login-title" className="mb-1 text-lg font-bold text-slate-900">
              Ingresar
            </h2>
            <p className="mb-6 text-sm text-slate-600">
              Usa el correo institucional con el que te registraste.
            </p>

            {justResetPassword && (
              <div
                role="status"
                className="mb-4 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800"
              >
                Tu contraseña fue actualizada. Ingresa con la nueva.
              </div>
            )}

            {accesoRetirado && (
              <div
                role="alert"
                className="mb-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
              >
                <p className="font-semibold">Tu acceso a Kaptar fue retirado.</p>
                <p className="mt-1">
                  El administrador de tu municipio te quitó del equipo. Si crees que es un error,
                  pídele que te invite de nuevo o escríbenos a{" "}
                  <a href="mailto:soporte@kaptar.com.co" className="font-semibold underline">
                    soporte@kaptar.com.co
                  </a>
                  .
                </p>
              </div>
            )}

            <label className="mb-3 block">
              <span className="mb-1 block text-sm font-medium text-slate-700">
                Correo institucional
              </span>
              <input
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-brand-600 focus:ring-1 focus:ring-brand-600 focus:outline-hidden"
                placeholder="planeacion@tumunicipio.gov.co"
              />
            </label>

            <label className="mb-2 block">
              <span className="mb-1 block text-sm font-medium text-slate-700">Contraseña</span>
              <input
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-brand-600 focus:ring-1 focus:ring-brand-600 focus:outline-hidden"
              />
            </label>

            <div className="mb-5 flex justify-end">
              <Link
                to="/forgot-password"
                className="text-xs font-medium text-brand-700 hover:text-brand-800 hover:underline"
              >
                ¿Olvidaste tu contraseña?
              </Link>
            </div>

            {/* Cloudflare Turnstile — invisible para 99% de usuarios.
                Si VITE_TURNSTILE_SITE_KEY no está seteada (dev/CI sin
                Cloudflare), el widget no renderiza y signIn pasa sin
                captchaToken — Supabase decide si aceptar o rechazar
                según su config del Dashboard. */}
            {TURNSTILE_SITE_KEY && (
              <div className="mb-4 flex justify-center">
                <Turnstile
                  ref={turnstileRef}
                  siteKey={TURNSTILE_SITE_KEY}
                  options={{
                    theme: "light",
                    size: "flexible",
                  }}
                  onSuccess={(token) => setCaptchaToken(token)}
                  onError={() => setCaptchaToken(null)}
                  onExpire={() => setCaptchaToken(null)}
                />
              </div>
            )}

            {errorMsg && (
              <div
                role="alert"
                className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800"
              >
                {errorMsg}
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full rounded-md bg-cta-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-cta-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isSubmitting ? "Ingresando…" : "Continuar"}
            </button>

            <p className="mt-6 text-center text-xs text-slate-500">
              ¿Problemas para ingresar? Escribe a{" "}
              <a
                href="mailto:soporte@kaptar.com.co"
                className="font-medium text-brand-700 hover:underline"
              >
                soporte@kaptar.com.co
              </a>
              .
            </p>
          </form>

          <p data-testid="login-legal" className="mt-4 text-center text-xs text-slate-500">
            Al ingresar se aplican los{" "}
            <Link
              to="/legal/terminos"
              target="_blank"
              className="font-medium text-brand-700 hover:underline"
            >
              Términos de uso
            </Link>{" "}
            y la{" "}
            <Link
              to="/legal/privacidad"
              target="_blank"
              className="font-medium text-brand-700 hover:underline"
            >
              Política de privacidad
            </Link>{" "}
            de Kaptar.
          </p>
        </div>
      </main>

      {/* Columna derecha: propuesta de valor + métricas (1/4 del ancho).
          Las métricas se apilan verticalmente porque el panel es angosto
          (~25% del viewport). Heading y tagline también más compactos. */}
      <aside
        className="hidden flex-col justify-between bg-slate-900 p-8 text-white lg:col-span-1 lg:flex xl:p-10"
        aria-hidden="true"
      >
        <div>
          <div className="mb-2 flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-md bg-brand-600">
              <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5">
                <path d="M12 3L4 8l8 5 8-5-8-5z" fill="white" opacity="0.9" />
                <path d="M4 13l8 5 8-5" stroke="white" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
            </div>
            <span className="text-lg font-bold tracking-tight">Kaptar</span>
          </div>
        </div>

        <div>
          <h2 className="mb-3 text-2xl leading-tight font-extrabold tracking-tight xl:text-3xl">
            La verdad sobre los recursos de tu municipio
          </h2>
          <p className="mb-8 text-sm leading-relaxed text-slate-300">
            Descubre, evalúa y postula a las convocatorias públicas que aplican a tu municipio.
          </p>

          <dl className="flex flex-col gap-3">
            <div className="rounded-lg border border-slate-700 bg-slate-800/50 p-3">
              <dt className="text-xl font-extrabold text-brand-400">$30.9B</dt>
              <dd className="mt-0.5 text-[10px] font-medium tracking-wider text-slate-400 uppercase">
                Bienal SGR 2025-2026
              </dd>
            </div>
            <div className="rounded-lg border border-slate-700 bg-slate-800/50 p-3">
              <dt className="text-xl font-extrabold text-brand-400">1.100+</dt>
              <dd className="mt-0.5 text-[10px] font-medium tracking-wider text-slate-400 uppercase">
                Municipios en Colombia
              </dd>
            </div>
            <div className="rounded-lg border border-slate-700 bg-slate-800/50 p-3">
              <dt className="text-xl font-extrabold text-brand-400">6</dt>
              <dd className="mt-0.5 text-[10px] font-medium tracking-wider text-slate-400 uppercase">
                Fuentes oficiales
              </dd>
            </div>
          </dl>
        </div>

        <footer className="text-[10px] font-medium tracking-wider text-slate-500 uppercase">
          <p>Conexión cifrada</p>
          <p className="mt-1">Aislamiento por municipio</p>
          <p className="mt-1">Monitoreo continuo</p>
        </footer>
      </aside>
    </div>
  );
}

/** Traduce mensajes de Supabase a español más amigable. */
function humanizeError(msg: string): string {
  const m = msg.toLowerCase();
  if (m.includes("invalid login")) {
    return "Credenciales inválidas. Revisa correo y contraseña.";
  }
  if (m.includes("email not confirmed")) {
    return "Tu correo aún no fue confirmado. Revisa tu bandeja de entrada.";
  }
  if (m.includes("rate limit")) {
    return "Demasiados intentos. Espera unos minutos antes de reintentar.";
  }
  if (m.includes("captcha")) {
    return "La verificación de seguridad falló. Recarga la página e intenta de nuevo.";
  }
  return msg;
}
